import React from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import { BoxProps, IconButton } from '@mui/material';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { SelectionTreeT } from 'business/definitions';
import { useTranslation } from 'react-i18next';
import LockIcon from '@mui/icons-material/Lock';
import StyAdornedInput from './StyAdornedInput';

const StyChip = styled(StyAdornedInput, {
    name: 'StyChip',
    shouldForwardProp: (prop) => prop !== 'isBadValue',
})<{ isBadValue?: boolean } & BoxProps>(({ isBadValue, theme }) => ({
    backgroundColor: theme.palette.primary.background,
    color: theme.palette.primary.main,
    ...(isBadValue ? { background: 'lightgray' } : {}),
    borderRadius: '50px',
}));

const StyLabel = styled('span', {
    name: 'StyLabel',
})({
    overflow: 'hidden',
    width: 'fit-content',
    textOverflow: 'ellipsis',
    maxHeight: '1.5rem',
    maxWidth: 'inherit',
    fontSize: '14px',
});

const StyCancelButton = styled(IconButton, {
    name: 'StyCancelButton',
})({
    color: '#94a0c8',
    marginLeft: '0.2rem',
    cursor: 'pointer',
    padding: '2px',
});

type Props = {
    option?: SelectionTreeT;
    onRemoveClick?: (arg1: SelectionTreeT) => void;
    isLocked?: boolean;
};

// TODO: Do we still need this component? MUI provides a Chip component, it's used only once and we don't pass 'option' or 'onRemoveClick' to this component.
const Chip = ({
    option = {
        isBadValue: false,
        label: '',
        value: '',
        isSubtreeSelected: false,
    },
    onRemoveClick = () => {},
    isLocked = false,
}: Props): React.ReactElement => {
    const { t } = useTranslation();

    const tooltip =
        !isLocked && option?.isBadValue ? (
            <Tooltip
                title={t('common.invalidFilterValue')}
                placement="bottom-start"
                enterDelay={50}
            >
                <HelpRoundedIcon
                    sx={{ color: '#1e2eb8', mr: '0.5rem' }}
                    fontSize="small"
                />
            </Tooltip>
        ) : (
            <></>
        );

    const lockedIcon = isLocked ? (
        <Tooltip
            title={t('common.lockedFilterValue')}
            placement="bottom-start"
            enterDelay={50}
        >
            <LockIcon />
        </Tooltip>
    ) : (
        <></>
    );

    const removeButton = (
        <StyCancelButton
            onClick={(event) => {
                // stop the propagation of the event to its HTML 's parents -> so the TreeTableModal onClick is not called
                event.stopPropagation();
                if (!isLocked) {
                    if (onRemoveClick) onRemoveClick(option!);
                }
            }}
            size="large"
        >
            <CancelIcon sx={{ fontSize: '1.1rem' }} />
        </StyCancelButton>
    );

    return (
        <StyChip isBadValue={!!option?.isBadValue} tabIndex={-1}>
            {tooltip}
            {lockedIcon}
            <StyLabel> {option?.label || ''}</StyLabel>
            {removeButton}
        </StyChip>
    );
};

export default Chip;
